/**
 * Exchange Rate Service
 * Fetches live exchange rates from ExchangeRate-API
 */

const API_KEY = '13e1bd8bb2783d88ab311d0d';
const BASE_URL = 'https://v6.exchangerate-api.com/v6';
const CACHE_KEY = 'bonndle:exchange_rates';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

// Country code to currency code mapping
const COUNTRY_TO_CURRENCY = {
  US: 'USD',
  NG: 'NGN',
  GB: 'GBP',
  CA: 'CAD',
  AU: 'AUD',
  DE: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  NL: 'EUR',
  IN: 'INR',
  JP: 'JPY',
  CN: 'CNY',
  KR: 'KRW',
  BR: 'BRL',
  MX: 'MXN',
  ZA: 'ZAR',
  GH: 'GHS',
  KE: 'KES',
  AE: 'AED',
  SG: 'SGD',
  PH: 'PHP',
  PK: 'PKR',
  EG: 'EGP',
};

// Currency symbols
const CURRENCY_SYMBOLS = {
  USD: '$',
  NGN: '₦',
  GBP: '£',
  CAD: 'CA$',
  AUD: 'A$',
  EUR: '€',
  INR: '₹',
  JPY: '¥',
  CNY: '¥',
  KRW: '₩',
  BRL: 'R$',
  MXN: 'MX$',
  ZAR: 'R',
  GHS: 'GH₵',
  KES: 'KSh',
  AED: 'د.إ',
  SGD: 'S$',
  PHP: '₱',
  PKR: '₨',
  EGP: 'E£',
};

// Fallback rates in case API fails (USD base)
const FALLBACK_RATES = {
  USD: 1,
  NGN: 1550,
  GBP: 0.79,
  CAD: 1.36,
  AUD: 1.53,
  EUR: 0.92,
  INR: 83.12,
  JPY: 151.5,
  CNY: 7.24,
  KRW: 1340,
  BRL: 4.97,
  MXN: 17.15,
  ZAR: 18.65,
  GHS: 12.5,
  KES: 153,
  AED: 3.67,
  SGD: 1.34,
  PHP: 56.2,
  PKR: 278,
  EGP: 30.9,
};

/**
 * Get cached rates from localStorage
 */
function getCachedRates() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { rates, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - timestamp < CACHE_DURATION) {
      return rates;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Cache rates to localStorage
 */
function cacheRates(rates) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        rates,
        timestamp: Date.now(),
      })
    );
  } catch {
    // Ignore cache errors
  }
}

/**
 * Fetch exchange rates from API
 */
async function fetchRatesFromAPI() {
  try {
    const response = await fetch(`${BASE_URL}/${API_KEY}/latest/USD`);

    if (!response.ok) {
      throw new Error('Failed to fetch exchange rates');
    }

    const data = await response.json();

    if (data.result === 'success' && data.conversion_rates) {
      return data.conversion_rates;
    }

    throw new Error('Invalid API response');
  } catch (error) {
    console.error('Exchange rate API error:', error);
    return null;
  }
}

/**
 * Get exchange rates (cached or fresh)
 */
export async function getExchangeRates() {
  // Try cache first
  const cached = getCachedRates();
  if (cached) {
    return cached;
  }

  // Fetch from API
  const rates = await fetchRatesFromAPI();

  if (rates) {
    cacheRates(rates);
    return rates;
  }

  // Return fallback rates if API fails
  return FALLBACK_RATES;
}

/**
 * Get exchange rate for a specific currency
 */
export async function getExchangeRate(currencyCode) {
  const rates = await getExchangeRates();
  return rates[currencyCode] || 1;
}

/**
 * Get currency code for a country
 */
export function getCurrencyCode(countryCode) {
  return COUNTRY_TO_CURRENCY[countryCode] || 'USD';
}

/**
 * Get currency symbol
 */
export function getCurrencySymbol(currencyCode) {
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
}

/**
 * Convert USD to another currency
 */
export async function convertFromUSD(usdAmount, targetCurrency) {
  const rate = await getExchangeRate(targetCurrency);
  return usdAmount * rate;
}

/**
 * Convert USD to local currency based on country code
 */
export async function convertToLocalCurrency(usdAmount, countryCode) {
  const currencyCode = getCurrencyCode(countryCode);

  if (currencyCode === 'USD') {
    return {
      amount: usdAmount,
      formatted: `$${formatNumber(usdAmount)}`,
      code: 'USD',
      symbol: '$',
      rate: 1,
    };
  }

  const rate = await getExchangeRate(currencyCode);
  const localAmount = usdAmount * rate;
  const symbol = getCurrencySymbol(currencyCode);

  return {
    amount: localAmount,
    formatted: `${currencyCode} ${formatNumber(localAmount, currencyCode)}`,
    code: currencyCode,
    symbol,
    rate,
  };
}

/**
 * Format number with appropriate decimals
 */
function formatNumber(amount, currencyCode = 'USD') {
  // Currencies that don't use decimals
  const noDecimalCurrencies = ['JPY', 'KRW'];
  const decimals = noDecimalCurrencies.includes(currencyCode) ? 0 : 2;

  return amount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Check if country uses a different currency than USD
 */
export function hasDifferentCurrency(countryCode) {
  if (!countryCode) return false;
  return getCurrencyCode(countryCode) !== 'USD';
}

export default {
  getExchangeRates,
  getExchangeRate,
  getCurrencyCode,
  getCurrencySymbol,
  convertFromUSD,
  convertToLocalCurrency,
  hasDifferentCurrency,
};
